import React from 'react'

import { RouteParams, getVersionRenderStatus } from '@sketch/modules-common'
import { RouteComponentProps } from 'react-router-dom'

import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from '../../../../versioning'
import { useGetVersionQueryWithPolling } from '../../../operations'
import { SectionView } from '../SectionView'

type SectionQueriesRouteProps = RouteComponentProps<
  Partial<RouteParams<'PAGE_SECTION'>>
>

export interface SectionQueriesProps
  extends SectionQueriesRouteProps,
    DocumentSidebarLayoutExtraProps {}

/**
 * Entry point for the single-Section focus view - the Section equivalent of
 * FrameQueries. The section's own identity (name, isVariantSet) is fetched
 * via GQL (getSectionsForPage, in SectionView/SectionSelector/
 * SectionsNavigationItem); this component only needs the page/version data
 * that the plain page canvas view also needs.
 */
export const SectionQueries = ({
  match,
  ...layoutProps
}: SectionQueriesProps) => {
  const { pageUUID, sectionUUID } = match.params

  const versioning = useVersioning()
  const { share, versionShortId, isViewingLatestVersion } = versioning

  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const { data, error } = useGetVersionQueryWithPolling({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId,
    },
  })

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const renderStatus = getVersionRenderStatus(data?.share?.version)

  // The canvas and everything it needs is mounted once by ShareCanvasRoute,
  // above this and the other two views, so that moving between them does not
  // take the renderer with it.
  return (
    <SectionView
      layoutProps={layoutProps}
      share={share}
      currentVersion={currentVersion}
      pageUUID={pageUUID!}
      sectionUUID={sectionUUID!}
      isViewingLatestVersion={isViewingLatestVersion}
      renderStatus={renderStatus}
    />
  )
}
